import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { nekoPos } from 'public-contracts';
import { useFormContext } from 'react-hook-form';
import { useTableStatusStore } from '../../stores/useTableStatusStore';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';

const FIXED_TABLES = Array.from({ length: 16 }, (_, i) => i + 1);

interface ModernTableSelectorProps {
  disabled?: boolean;
  currentOrderId?: string; // ID of the current order being edited (if editing)
}

export const ModernTableSelector: React.FC<ModernTableSelectorProps> = ({
  disabled = false,
  currentOrderId,
}) => {
  const { translate } = useTranslation();
  const { watch, setValue } = useFormContext();
  const [showSelector, setShowSelector] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');

  const orderType = watch('orderType');
  const tableNumber = watch('tableNumber');

  // Sync input value when tableNumber changes
  useEffect(() => {
    if (tableNumber && !FIXED_TABLES.includes(tableNumber)) {
      setInputValue(tableNumber.toString());
    } else if (!tableNumber) {
      setInputValue('');
    }
  }, [tableNumber]);

  const { tableStatuses, fetchTableStatuses } = useTableStatusStore();

  // Fetch table statuses on component mount
  useEffect(() => {
    fetchTableStatuses();
  }, [fetchTableStatuses]); // Run once on mount

  // Refresh table statuses when modal opens
  useEffect(() => {
    if (showSelector) {
      fetchTableStatuses();
    }
  }, [showSelector, fetchTableStatuses]);

  // Create a map of table numbers to their status
  const tableStatusMap = tableStatuses.reduce((acc, status) => {
    acc[status.tableNumber] = status;
    return acc;
  }, {} as Record<number, nekoPos.TableStatus>);

  // Debug: Log table statuses when they change
  useEffect(() => {
    if (tableStatuses.length > 0) {
      console.log('[ModernTableSelector] Table statuses:', tableStatuses);
      console.log('[ModernTableSelector] Table status map:', tableStatusMap);
    }
  }, [tableStatuses, tableStatusMap]);

  const handleTableSelect = (selectedTableNumber: number) => {
    const status = tableStatusMap[selectedTableNumber];
    const isBookedByCurrentOrder =
      status?.orderId && currentOrderId && status.orderId === currentOrderId;
    const isBookedByOtherOrder =
      status?.orderId && (!currentOrderId || status.orderId !== currentOrderId);
    // Treat any table with an orderId as occupied unless it belongs to the current order
    const isAvailable =
      (!status || status.isAvailable || isBookedByCurrentOrder) &&
      !isBookedByOtherOrder;

    if (!isAvailable) return;

    setInputValue(''); // Clear input when selecting predefined table
    setValue('tableNumber', selectedTableNumber, { shouldDirty: true });
  };

  const handleCustomTableChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputVal = event.target.value;
    setInputValue(inputVal);

    const tableNumber = parseInt(inputVal);
    if (!isNaN(tableNumber) && tableNumber > 0) {
      // Check if the table is available
      const status = tableStatusMap[tableNumber];
      const isBookedByCurrentOrder =
        status?.orderId && currentOrderId && status.orderId === currentOrderId;
      const isBookedByOtherOrder =
        status?.orderId &&
        (!currentOrderId || status.orderId !== currentOrderId);
      // Treat any table with an orderId as occupied unless it belongs to the current order
      const isAvailable =
        (!status || status.isAvailable || isBookedByCurrentOrder) &&
        !isBookedByOtherOrder;

      if (!isAvailable) {
        // Don't allow selection of unavailable tables
        return;
      }

      setValue('tableNumber', tableNumber, { shouldDirty: true });
    } else if (inputVal === '') {
      setValue('tableNumber', undefined, { shouldDirty: true });
    }
  };

  const clearTableSelection = () => {
    setInputValue('');
    setValue('tableNumber', undefined, { shouldDirty: true });
  };

  if (orderType !== 'dine_in') return null;

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {translate('pos.orders.fields.tableNumber')}
            </CardTitle>
            {!disabled && (
              <Button
                onClick={() => setShowSelector(true)}
                size="sm"
                variant={tableNumber ? 'outline' : 'default'}
              >
                {tableNumber
                  ? translate('pos.orders.actions.changeTable')
                  : translate('pos.orders.actions.selectTable')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {tableNumber ? (
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/50"
                >
                  {tableNumber}
                </Badge>
                <span className="text-sm text-green-700 dark:text-green-300">
                  {translate('pos.orders.fields.tableNumber')} đã chọn
                </span>
              </div>
              {!disabled && (
                <Button
                  onClick={clearTableSelection}
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive/80"
                >
                  ×
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              {translate('pos.orders.messages.noTableSelected')}
            </div>
          )}
        </CardContent>
      </Card>

      {showSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">
                  {translate('pos.orders.fields.tableNumber')}
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSelector(false)}
                >
                  {translate('pos.common.action.close')}
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              <div className="max-h-[70vh] overflow-y-auto p-4">
                {/* Fixed Tables Grid */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-foreground mb-3">
                    {translate(
                      'pos.orders.actions.fixedTables',
                      'Fixed Tables (1-16)'
                    )}
                  </h3>
                  <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    {FIXED_TABLES.map((number) => {
                      const status = tableStatusMap[number];
                      const isSelected = tableNumber === number;
                      const isBookedByCurrentOrder =
                        status?.orderId &&
                        currentOrderId &&
                        status.orderId === currentOrderId;
                      const isBookedByOtherOrder =
                        status?.orderId &&
                        (!currentOrderId || status.orderId !== currentOrderId);
                      // Table is unavailable if:
                      // 1. It's booked by another order, OR
                      // 2. It has a status with isAvailable=false and is not booked by current order
                      const isUnavailable =
                        isBookedByOtherOrder ||
                        (status &&
                          status.isAvailable === false &&
                          !isBookedByCurrentOrder);

                      return (
                        <div key={number} className="relative">
                          <Button
                            variant={isSelected ? 'default' : 'outline'}
                            size="sm"
                            className={`h-12 w-full ${
                              isUnavailable
                                ? 'opacity-50 cursor-not-allowed bg-muted text-muted-foreground'
                                : isSelected
                                ? 'bg-primary text-primary-foreground'
                                : isBookedByCurrentOrder
                                ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-800 hover:bg-yellow-100 dark:hover:bg-yellow-900/50'
                                : 'hover:bg-accent'
                            }`}
                            onClick={() =>
                              !isUnavailable && handleTableSelect(number)
                            }
                            disabled={isUnavailable || disabled}
                            title={
                              isBookedByOtherOrder
                                ? translate(
                                    'pos.orders.messages.tableBookedByOrder',
                                    `Table booked by order ${status.orderId}`
                                  )
                                : isBookedByCurrentOrder
                                ? translate(
                                    'pos.orders.messages.tableBookedByThisOrder',
                                    'Table booked by this order'
                                  )
                                : undefined
                            }
                          >
                            {number}
                          </Button>
                          {isBookedByOtherOrder && (
                            <Badge
                              className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-xs bg-orange-500 text-white"
                              title={translate(
                                'pos.orders.messages.tableBookedByOrder',
                                `Booked by order ${status.orderId}`
                              )}
                            >
                              !
                            </Badge>
                          )}
                          {isBookedByCurrentOrder && (
                            <Badge
                              className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-xs bg-yellow-500 text-white"
                              title={translate(
                                'pos.orders.messages.tableBookedByThisOrder',
                                'Booked by this order'
                              )}
                            >
                              ✓
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <Separator className="my-4" />

                {/* Custom Table Input */}
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-3">
                    {translate('pos.orders.messages.customTableNumber')}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      value={inputValue}
                      onChange={handleCustomTableChange}
                      placeholder={translate(
                        'pos.orders.messages.enterTableNumber'
                      )}
                      className="flex-1"
                      disabled={disabled}
                    />
                    {inputValue && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={clearTableSelection}
                        disabled={disabled}
                      >
                        ×
                      </Button>
                    )}
                  </div>

                  {inputValue &&
                    (() => {
                      const tableNumber = parseInt(inputValue);
                      if (!isNaN(tableNumber)) {
                        const status = tableStatusMap[tableNumber];
                        const isBookedByCurrentOrder =
                          status?.orderId &&
                          currentOrderId &&
                          status.orderId === currentOrderId;
                        const isBookedByOtherOrder =
                          status?.orderId &&
                          (!currentOrderId ||
                            status.orderId !== currentOrderId);
                        // Treat any table with an orderId as occupied unless it belongs to the current order
                        const isAvailable =
                          (!status ||
                            status.isAvailable ||
                            isBookedByCurrentOrder) &&
                          !isBookedByOtherOrder;
                        if (!isAvailable) {
                          return (
                            <p className="text-sm text-destructive mt-2">
                              {isBookedByOtherOrder
                                ? translate(
                                    'pos.orders.messages.tableBookedByOrder',
                                    `Table is booked by order ${status.orderId}`
                                  )
                                : translate(
                                    'pos.orders.messages.tableOccupied'
                                  )}
                            </p>
                          );
                        } else if (isBookedByCurrentOrder) {
                          return (
                            <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                              {translate(
                                'pos.orders.messages.tableBookedByThisOrder',
                                'Table is booked by this order'
                              )}
                            </p>
                          );
                        }
                      }
                      return null;
                    })()}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};
